import { create } from 'zustand'

export type NotificationType = 
  | 'agent_forked'
  | 'access_request'
  | 'access_accepted'
  | 'internal_message'
  | 'agent_inactive'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  read: boolean
  createdAt: string
  link?: string
  metadata?: Record<string, any>
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isOpen: boolean
  isLoading: boolean
  fetchNotifications: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (id: string) => Promise<void>
  toggleDropdown: () => void
  closeDropdown: () => void
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  isLoading: false,

  fetchNotifications: async () => {
    set({ isLoading: true })
    try {
      const authStorage = localStorage.getItem('auth-storage')
      const token = authStorage ? JSON.parse(authStorage).state?.token : null
      
      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        // * If 404, API might not be implemented yet - return empty array
        if (response.status === 404) {
          set({ notifications: [], unreadCount: 0, isLoading: false })
          return
        }
        throw new Error('Failed to fetch notifications')
      }
      
      const data = await response.json()
      const notifications = data.notifications || data.data?.notifications || []
      const unreadCount = notifications.filter((n: Notification) => !n.read).length
      
      set({ 
        notifications,
        unreadCount,
        isLoading: false 
      })
    } catch (error) {
      console.error('Error fetching notifications:', error)
      // * On error, set empty array instead of failing
      set({ notifications: [], unreadCount: 0, isLoading: false })
    }
  },

  markAsRead: async (id: string) => {
    try {
      const authStorage = localStorage.getItem('auth-storage')
      const token = authStorage ? JSON.parse(authStorage).state?.token : null
      
      const response = await fetch(`/api/notifications/${id}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      // * Optimistically update UI even if API call fails
      set((state) => {
        const updatedNotifications = state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        )
        const unreadCount = updatedNotifications.filter((n) => !n.read).length
        return {
          notifications: updatedNotifications,
          unreadCount,
        }
      })
      
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to mark notification as read')
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  },

  markAllAsRead: async () => {
    try {
      const authStorage = localStorage.getItem('auth-storage')
      const token = authStorage ? JSON.parse(authStorage).state?.token : null
      
      // * Optimistically update UI
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      }))
      
      const response = await fetch('/api/notifications/read-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to mark all notifications as read')
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  },

  deleteNotification: async (id: string) => {
    try {
      const authStorage = localStorage.getItem('auth-storage')
      const token = authStorage ? JSON.parse(authStorage).state?.token : null
      
      // * Optimistically update UI
      set((state) => {
        const updatedNotifications = state.notifications.filter((n) => n.id !== id)
        const unreadCount = updatedNotifications.filter((n) => !n.read).length
        return {
          notifications: updatedNotifications,
          unreadCount,
        }
      })
      
      const response = await fetch(`/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to delete notification')
      }
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  },

  toggleDropdown: () => {
    set((state) => {
      const newIsOpen = !state.isOpen
      if (newIsOpen && state.notifications.length === 0) {
        // * Fetch notifications when opening dropdown for the first time
        get().fetchNotifications()
      }
      return { isOpen: newIsOpen }
    })
  },

  closeDropdown: () => {
    set({ isOpen: false })
  },
}))
